/**
 * useGuidanceData Hook
 *
 * Fetches per-company guidance vs actuals data for Security Analysis.
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { handleSessionExpired } from '../utils/authHelpers';
import {
  FREE_SECURITY_ANALYSIS_DATASETS,
  getFreeSecurityAnalysisCdnUrl,
  getFreeSecurityAnalysisFileKey,
  getGuidanceFileKey,
  isPublicResearchSampleTicker,
} from '../constants/api';
import { getCachedData, setCachedData, getGuidanceCacheKey } from '../utils/cache';
import { fetchJson, loadLocalOrFixtureJson, shouldUseLocalOrFixtureData } from '../utils/dataLoader';
import { buildGuidanceCompanyPreviewData } from '../features/guidance/guidancePreviewData';
import { fetchResearchJsonFile } from '../utils/authenticatedApi';
import { useSubscriptionStatus } from './useSubscriptionStatus';

const EMPTY_GUIDANCE = {
  meta: null,
  guidance: {},
  actuals: {},
  guidanceVsActuals: {},
};

export function useGuidanceData(ticker, options = {}) {
  const { enabled = true } = options;

  const { getAccessTokenSilently } = useAuth0();
  const {
    isPaid,
    hasResearchAccess,
    isAuthenticated,
  } = useSubscriptionStatus();
  const hasRequiredAccess = hasResearchAccess;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(EMPTY_GUIDANCE);

  const applyGuidanceData = useCallback((guidanceData) => {
    setData({
      meta: guidanceData?.meta || null,
      guidance: guidanceData?.guidance || {},
      actuals: guidanceData?.actuals || {},
      guidanceVsActuals: guidanceData?.guidanceVsActuals || {},
    });
  }, []);

  useEffect(() => {
    if (!enabled || !ticker) return;

    const upperTicker = String(ticker).toUpperCase();

    const fetchGuidanceData = async () => {
      const publicSample = isPublicResearchSampleTicker(upperTicker);
      if (!hasRequiredAccess && publicSample) {
        setLoading(true);
        setError(null);
        try {
          const guidanceData = shouldUseLocalOrFixtureData()
            ? await loadLocalOrFixtureJson(`data/${getFreeSecurityAnalysisFileKey(
              upperTicker,
              FREE_SECURITY_ANALYSIS_DATASETS.GUIDANCE,
            )}`)
            : await fetchJson(
              getFreeSecurityAnalysisCdnUrl(
                upperTicker,
                FREE_SECURITY_ANALYSIS_DATASETS.GUIDANCE,
              ),
              `${upperTicker} guidance sample`,
            );
          applyGuidanceData(guidanceData);
        } catch (err) {
          setError(err);
          applyGuidanceData(EMPTY_GUIDANCE);
        } finally {
          setLoading(false);
        }
        return;
      }

      if (!hasRequiredAccess) {
        applyGuidanceData(buildGuidanceCompanyPreviewData(upperTicker));
        return;
      }

      if (shouldUseLocalOrFixtureData()) {
        setLoading(true);
        setError(null);
        try {
          const guidanceData = await loadLocalOrFixtureJson(`data/stockpage/guidance/${upperTicker}.json`);
          applyGuidanceData(guidanceData);
        } catch (err) {
          console.error(`Error loading local guidance/${upperTicker}.json`, err);
          setError(err);
          applyGuidanceData(EMPTY_GUIDANCE);
        } finally {
          setLoading(false);
        }
        return;
      }

      const cacheKey = getGuidanceCacheKey(upperTicker);
      const cached = getCachedData(cacheKey);
      if (cached) {
        applyGuidanceData(cached);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently({
          audience: 'https://api.softwareiq.io',
          scope: 'read:news',
        });

        const guidanceData = await fetchResearchJsonFile(getGuidanceFileKey(upperTicker), token);
        setCachedData(cacheKey, guidanceData);
        applyGuidanceData(guidanceData);
      } catch (err) {
        console.error('Error fetching guidance data', err);
        setError(err);
        applyGuidanceData(EMPTY_GUIDANCE);
        if (
          err.message?.includes('Missing Refresh Token') ||
          err.error === 'login_required' ||
          err?.response?.status === 401
        ) {
          handleSessionExpired();
        }
      } finally {
        setLoading(false);
      }
    };

    fetchGuidanceData();
  }, [enabled, ticker, hasRequiredAccess, isAuthenticated, getAccessTokenSilently, applyGuidanceData]);

  return {
    ...data,
    loading,
    error,
    isPaid,
    hasResearchAccess,
    isAuthenticated,
    isPreview: !hasRequiredAccess && !isPublicResearchSampleTicker(ticker),
  };
}

export default useGuidanceData;
